import { EventTarget } from "event-target-shim";
import { throttle } from "lodash";
import { Node as ProseNode } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { Extension } from "../extension";
import { collapseChanged } from "./collapse";

export interface ViewDesc {
	node: ProseNode | null;
	parent?: ViewDesc;
	dom: Node;
	posAtStart: number;
	posAtEnd: number;
	posAfter: number;
	posBefore: number;
	dirty: number;
}

const MousePosKey = new PluginKey<EventTarget>("MousePos");

class MousePos extends Extension {
	anchor: ViewDesc | null;
	emitter: EventTarget;
	leaveBoundary?: HTMLElement | null;

	get name() {
		return "mousePos";
	}
	get defaultOptions() {
		return {
			getLeaveBoundary: (dom: HTMLElement) => dom.closest("article"),
			mouseAnchorClass: "prose-mouse-anchor",
		};
	}

	static subscribe = (view: EditorView, cb: EventListenerOrEventListenerObject) => {
		const emitter = MousePosKey.getState(view.state);
		if (emitter) emitter.addEventListener("anchorChange", cb);
	};
	static unsubscribe = (view: EditorView, cb: EventListenerOrEventListenerObject) => {
		const emitter = MousePosKey.getState(view.state);
		if (emitter) emitter.removeEventListener("anchorChange", cb);
	};

	constructor(options?: {}) {
		super(options);
		this.anchor = null;
		this.emitter = new EventTarget();
	}

	get plugins() {
		const extension = this;
		const mousePosPlugin = new Plugin<EventTarget>({
			state: {
				init: () => extension.emitter,
				apply: (tr, v) => {
					if (tr.selectionSet && extension.editorView && extension.editorView.hasFocus()) {
						this.emitChange(null);
					} else if (extension.anchor?.node && collapseChanged(tr, extension.anchor.node)) {
						this.emitChange(extension.anchor);
					}
					return v;
				},
			},
			key: MousePosKey,
			props: {
				handleTextInput: () => {
					if (extension.anchor) {
						extension.emitChange(null);
					}
					return false;
				},
				handleDOMEvents: {
					mouseover: (view, event) => {
						if (event instanceof MouseEvent) {
							extension.onMouseEnter(view, event);
						}
						return false;
					},
				},
			},
		});
		return [mousePosPlugin];
	}

	getAnchor = (view: EditorView, domNode: Node | null): (Node & { pmViewDesc: ViewDesc }) | null => {
		if (!domNode) return null;
		let parentNode: (Node & { pmViewDesc?: ViewDesc }) | null = domNode;
		const hasViewDesc = (n: Node & { pmViewDesc?: ViewDesc }): n is Node & { pmViewDesc: ViewDesc } =>
			Boolean(n.pmViewDesc);
		while (parentNode && parentNode !== view.dom) {
			if (hasViewDesc(parentNode) && parentNode.pmViewDesc.node && "blockId" in parentNode.pmViewDesc.node.attrs) {
				return parentNode;
			}
			parentNode = parentNode.parentElement;
		}
		return null;
	};

	_onMouseEnter = (view: EditorView, event: MouseEvent) => {
		if (view.dragging) return;
		if (!(event.target instanceof Node)) return;
		const anchorDOM = this.getAnchor(view, event.target);
		if (anchorDOM && anchorDOM.pmViewDesc !== this.anchor) {
			this.emitChange(anchorDOM.pmViewDesc);
			return;
		}
	};
	onMouseEnter = throttle(this._onMouseEnter, 100, { leading: true });
	onMouseLeave = (_event: MouseEvent) => {
		this.onMouseEnter.cancel();
		this.emitChange(null);
	};
	emitChange = (anchor: ViewDesc | null) => {
		this.anchor = anchor;
		this.emitter.dispatchEvent(new CustomEvent<ViewDesc | null>("anchorChange", { detail: anchor }));
	};
	init() {
		if (!this.editorView) return;
		const leaveBoundary = (this.leaveBoundary = this.options.getLeaveBoundary(this.editorView.dom));
		if (leaveBoundary) {
			leaveBoundary.addEventListener("mouseleave", this.onMouseLeave);
		} else {
			console.warn("mousePos plugin found no leaveBoundary");
		}
	}
	destroy() {
		if (this.leaveBoundary) {
			this.leaveBoundary.removeEventListener("mouseleave", this.onMouseLeave);
		}
	}
}

export default MousePos;
